import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import VLFCard from "@/components/Card";
import Breadcrumbs from "@/components/Breadcrumbs";
import DataTable from "@/components/DataTable";
import EmptyData from "@/components/EmptyData";
import Kpi, {KpiRow} from "@/components/Kpi";
import MainActions from "@/components/MainActions";
import StatusBadge from "@/components/StatusBadge";
import {DsBar} from "@/components/DsKit";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {ContractAPI} from "@/api/contract";
import {RosterAPI} from "@/api/roster";
import {visibleRosters} from "@/utils/functions";
import {
	dateFormat,
	ERROR_OPERATION,
	getInternalClubId,
	REQUIRED_FIELD,
} from "@/utils/constants";

import "./index.scss";

// ── Formatters ────────────────────────────────────────────────────────────────

const fmt = (n) =>
	n != null
		? "€\u00A0" +
			Number(n).toLocaleString("it-IT", {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			})
		: "---";

const fmtSaldo = (n) => {
	if (n == null) return "---";
	if (n > 0) return "+" + fmt(n);
	if (n < 0) return "−" + fmt(Math.abs(n));
	return fmt(0);
};

// ── Contract helpers ──────────────────────────────────────────────────────────

const contractTypeLabels = {
	professional: "Professionistico",
	amateur: "Dilettantistico",
	youth: "Settore giovanile",
	loan: "Prestito",
	amateur_professional: "Semiprofessionistico",
};

const SALARY_BANDS = [
	{label: "< 500 €/m", min: 0, max: 500},
	{label: "500 – 1.500 €/m", min: 500, max: 1500},
	{label: "1.500 – 3.000 €/m", min: 1500, max: 3000},
	{label: "> 3.000 €/m", min: 3000, max: Infinity},
];

function buildYearRows(contracts) {
	if (!contracts.length) return [];
	const currentYear = moment().year();
	let maxYear = currentYear;
	contracts.forEach((c) => {
		if (c.endDate) {
			const y = moment(c.endDate).year();
			if (y > maxYear) maxYear = y;
		}
	});
	const rows = [];
	for (let year = currentYear; year <= maxYear; year++) {
		const yearStart = moment(`${year}-01-01`);
		const yearEnd = moment(`${year}-12-31`);
		let totalCost = 0;
		const active = [];
		contracts.forEach((c) => {
			if (!c.totalMonthlySalary) return;
			const start = c.startDate ? moment(c.startDate) : moment().startOf("day");
			const end = c.endDate ? moment(c.endDate) : moment().endOf("year");
			if (start.isAfter(yearEnd) || end.isBefore(yearStart)) return;
			const overlapStart = moment.max(start, yearStart);
			const overlapEnd = moment.min(end, yearEnd);
			const months = Math.ceil(overlapEnd.diff(overlapStart, "months", true));
			if (months <= 0) return;
			totalCost += c.totalMonthlySalary * months;
			active.push(c);
		});
		rows.push({year, totalCost, contractCount: active.length});
	}
	return rows;
}

// ── Movements (localStorage) ──────────────────────────────────────────────────

const MOVEMENTS_KEY = "finance_movements_v1";
const BUDGET_KEY = "finance_budget_v1";

const MOVEMENT_TYPES = [
	{value: "acquisto", label: "Acquisto", direction: "out"},
	{value: "cessione", label: "Cessione", direction: "in"},
	{value: "prestito_in", label: "Prestito in entrata", direction: "out"},
	{value: "prestito_out", label: "Prestito in uscita", direction: "in"},
	{value: "bonus_attivo", label: "Bonus da incassare", direction: "in"},
	{value: "bonus_passivo", label: "Bonus da pagare", direction: "out"},
	{value: "altro", label: "Altro", direction: null},
];

const DIRECTIONS = [
	{value: "in", label: "Entrata"},
	{value: "out", label: "Uscita"},
];

function loadMovements() {
	try {
		return JSON.parse(localStorage.getItem(MOVEMENTS_KEY)) || [];
	} catch {
		return [];
	}
}
function saveMovements(data) {
	localStorage.setItem(MOVEMENTS_KEY, JSON.stringify(data));
}
function loadBudget() {
	try {
		const b = localStorage.getItem(BUDGET_KEY);
		return b !== null ? Number(b) : null;
	} catch {
		return null;
	}
}
function saveBudget(val) {
	localStorage.setItem(BUDGET_KEY, String(val));
}

// ── Movement modal ────────────────────────────────────────────────────────────

const EMPTY_MV = {
	date: moment().format("YYYY-MM-DD"),
	playerName: "",
	type: "cessione",
	direction: "in",
	amount: "",
	note: "",
};

function MovementModal({open, onClose, onSave, initialData}) {
	const [f, setF] = useState(EMPTY_MV);
	const [errors, setErrors] = useState([]);

	useEffect(() => {
		if (!open) return;
		setF(
			initialData
				? {
						date: initialData.date || moment().format("YYYY-MM-DD"),
						playerName: initialData.playerName || "",
						type: initialData.type || "cessione",
						direction: initialData.direction || "in",
						amount:
							initialData.amount != null ? String(initialData.amount) : "",
						note: initialData.note || "",
					}
				: EMPTY_MV,
		);
		setErrors([]);
	}, [open, initialData]);

	const set = (k, v) => setF((p) => ({...p, [k]: v}));
	const selectedMt = MOVEMENT_TYPES.find((t) => t.value === f.type);
	const resolvedDir = selectedMt?.direction ?? f.direction;

	const handleSave = () => {
		const errs = [];
		if (!f.playerName.trim()) errs.push("playerName");
		if (!f.date) errs.push("date");
		if (!f.amount || isNaN(parseFloat(f.amount)) || parseFloat(f.amount) <= 0)
			errs.push("amount");
		setErrors(errs);
		if (errs.length) return;
		onSave({...f, amount: parseFloat(f.amount), direction: resolvedDir});
	};

	const fieldError = (k) =>
		errors.includes(k) ? {error: true, helperText: REQUIRED_FIELD} : {};

	return (
		<VLFModal
			maxWidth="xs"
			open={open}
			title={initialData ? "Modifica movimento" : "Nuovo movimento"}
			close={onClose}
			confirm={handleSave}
			content={
				<div className="row">
					<div className="col-12">
						<VLFDatePicker
							label="Data"
							required
							value={f.date ? moment(f.date).toDate() : null}
							onChange={(v) =>
								set("date", v ? moment(v).format("YYYY-MM-DD") : "")
							}
							{...fieldError("date")}
						/>
					</div>
					<div className="col-12">
						<VLFTextField
							label="Giocatore"
							required
							placeholder="Nome giocatore"
							value={f.playerName}
							onChange={(e) => set("playerName", e.target.value)}
							{...fieldError("playerName")}
						/>
					</div>
					<div className="col-12">
						<VLFSelectNew
							label="Tipo"
							options={MOVEMENT_TYPES}
							sorted={false}
							isClearable={false}
							value={MOVEMENT_TYPES.find((t) => t.value === f.type)}
							onChange={(v) => set("type", v?.value || "altro")}
						/>
					</div>
					{!selectedMt?.direction && (
						<div className="col-12">
							<VLFSelectNew
								label="Entrata o uscita"
								options={DIRECTIONS}
								sorted={false}
								isClearable={false}
								value={DIRECTIONS.find((d) => d.value === f.direction)}
								onChange={(v) => set("direction", v?.value || "in")}
							/>
						</div>
					)}
					<div className="col-12">
						<VLFTextField
							label="Importo (€)"
							required
							type="number"
							placeholder="0"
							value={f.amount}
							onChange={(e) => set("amount", e.target.value)}
							{...(errors.includes("amount")
								? {
										error: true,
										helperText: "Inserisci un importo maggiore di zero",
									}
								: {})}
						/>
					</div>
					<div className="col-12">
						<VLFTextField
							label="Note"
							placeholder="Facoltative"
							multiline
							minRows={2}
							maxRows={4}
							value={f.note}
							onChange={(e) => set("note", e.target.value)}
						/>
					</div>
				</div>
			}
		/>
	);
}

// ── Main page ─────────────────────────────────────────────────────────────────

function FinancePage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [contracts, setContracts] = useState([]);
	const [rosters, setRosters] = useState([]);
	const [selectedRosterId, setSelectedRosterId] = useState(null);
	const [rosterPlayerIds, setRosterPlayerIds] = useState(null); // null = tutti

	const [movements, setMovements] = useState([]);
	const [budget, setBudget] = useState(null);
	const [movementModal, setMovementModal] = useState(false);
	const [editingMovement, setEditingMovement] = useState(null);
	const [deleteConfirm, setDeleteConfirm] = useState(null);
	const [budgetEditOpen, setBudgetEditOpen] = useState(false);
	const [budgetInput, setBudgetInput] = useState("");
	const [financeView, setFinanceView] = useState("contracts"); // "contracts" | "movements"

	// Load
	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const [data, rosterList] = await Promise.all([
					ContractAPI.expiring(36500),
					RosterAPI.list({
						seasonId,
						prospect: false,
						clubId: getInternalClubId(),
					}),
				]);
				setContracts((data || []).filter((c) => (c.daysToExpiry ?? 1) >= 0));
				setRosters(
					visibleRosters(
						(rosterList || []).filter(
							(r) => !r.clubId || r.clubId === getInternalClubId(),
						),
					),
				);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			} finally {
				setLoading(false);
			}
		};
		load();
		setMovements(loadMovements());
		setBudget(loadBudget());
	}, [seasonId]);

	// When roster is selected, load its player IDs
	useEffect(() => {
		if (!selectedRosterId) {
			setRosterPlayerIds(null);
			return;
		}
		RosterAPI.getById(selectedRosterId)
			.then((r) => {
				const ids = new Set((r.players || []).map((p) => p.id || p.playerId));
				setRosterPlayerIds(ids);
			})
			.catch(() => setRosterPlayerIds(null));
	}, [selectedRosterId]);

	// Filtered contracts
	const filteredContracts = useMemo(
		() =>
			rosterPlayerIds
				? contracts.filter((c) => rosterPlayerIds.has(c.playerId))
				: contracts,
		[contracts, rosterPlayerIds],
	);

	// ── Derived: contracts ────────────────────────────────────────────────────

	const totalMonthly = useMemo(
		() =>
			filteredContracts.reduce((s, c) => s + (c.totalMonthlySalary || 0), 0),
		[filteredContracts],
	);
	const totalAnnual = totalMonthly * 12;
	const avgSalary = filteredContracts.length
		? totalMonthly / filteredContracts.length
		: 0;

	const topEarners = useMemo(
		() =>
			[...filteredContracts]
				.filter((c) => c.totalMonthlySalary)
				.sort((a, b) => b.totalMonthlySalary - a.totalMonthlySalary)
				.slice(0, 5),
		[filteredContracts],
	);
	const maxEarner = topEarners[0]?.totalMonthlySalary || 1;

	const byType = useMemo(() => {
		const counts = {};
		for (const c of filteredContracts) {
			counts[c.contractType] = (counts[c.contractType] || 0) + 1;
		}
		return Object.entries(counts).map(([type, count]) => ({type, count}));
	}, [filteredContracts]);

	const salaryBands = useMemo(
		() =>
			SALARY_BANDS.map((band) => ({
				...band,
				count: filteredContracts.filter((c) => {
					const s = c.totalMonthlySalary || 0;
					return s >= band.min && s < band.max;
				}).length,
			})),
		[filteredContracts],
	);
	const maxBandCount = Math.max(...salaryBands.map((b) => b.count), 1);

	const expiringSoon = useMemo(
		() =>
			filteredContracts
				.filter((c) => {
					if (!c.endDate) return false;
					const m = moment(c.endDate).diff(moment(), "months");
					return m >= 0 && m <= 12;
				})
				.sort((a, b) =>
					(a.playerLastName || "").localeCompare(b.playerLastName || "", "it"),
				),
		[filteredContracts],
	);

	const yearRows = useMemo(
		() => buildYearRows(filteredContracts),
		[filteredContracts],
	);

	const sortedContracts = useMemo(
		() =>
			[...filteredContracts].sort((a, b) => {
				// 1. Players with salary specified first
				const aHas = a.totalMonthlySalary != null ? 0 : 1;
				const bHas = b.totalMonthlySalary != null ? 0 : 1;
				if (aHas !== bHas) return aHas - bHas;
				// 2. Earliest expiry first (nulls last)
				if (a.endDate !== b.endDate) {
					if (!a.endDate) return 1;
					if (!b.endDate) return -1;
					return moment(a.endDate).diff(moment(b.endDate));
				}
				// 3. Alphabetical by last name
				return (a.playerLastName || "").localeCompare(
					b.playerLastName || "",
					"it",
				);
			}),
		[filteredContracts],
	);

	// ── Derived: budget ───────────────────────────────────────────────────────

	const budgetDiff = budget != null ? totalMonthly - budget : null;

	// ── Derived: movements ────────────────────────────────────────────────────

	const mvKpi = useMemo(() => {
		let entrate = 0,
			uscite = 0;
		for (const m of movements) {
			if (m.direction === "in") entrate += m.amount;
			else uscite += m.amount;
		}
		return {entrate, uscite, saldo: entrate - uscite};
	}, [movements]);

	const sortedMovements = useMemo(
		() => [...movements].sort((a, b) => new Date(b.date) - new Date(a.date)),
		[movements],
	);

	// ── Options ───────────────────────────────────────────────────────────────

	const rosterOptions = useMemo(
		() => [
			{value: null, label: "Tutte le rose"},
			...rosters.map((r) => ({
				value: r.id,
				label: r.name || r.playerCategory?.name,
			})),
		],
		[rosters],
	);

	// ── Handlers ─────────────────────────────────────────────────────────────

	const handleSaveMovement = (data) => {
		let updated;
		if (editingMovement) {
			updated = movements.map((m) =>
				m.id === editingMovement.id ? {...m, ...data} : m,
			);
		} else {
			updated = [...movements, {...data, id: String(Date.now())}];
		}
		saveMovements(updated);
		setMovements(updated);
		setMovementModal(false);
		setEditingMovement(null);
	};

	const handleDeleteMovement = (id) => {
		const updated = movements.filter((m) => m.id !== id);
		saveMovements(updated);
		setMovements(updated);
		setDeleteConfirm(null);
	};

	const handleSaveBudget = () => {
		const val = parseFloat(budgetInput);
		if (isNaN(val) || val < 0) return;
		saveBudget(val);
		setBudget(val);
		setBudgetEditOpen(false);
	};

	const headerActions =
		financeView === "contracts"
			? [
					{
						icon: <EditIcon />,
						title: "Budget ingaggi mensile",
						onClick: () => {
							setBudgetInput(budget != null ? String(budget) : "");
							setBudgetEditOpen(true);
						},
					},
				]
			: [
					{
						icon: <AddIcon />,
						title: "Nuovo movimento",
						onClick: () => {
							setEditingMovement(null);
							setMovementModal(true);
						},
					},
				];

	const contractName = (c) => `${c.playerLastName} ${c.playerFirstName}`;
	const toPlayer = (c) => navigate("/players/" + c.playerId);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Finanza"
				listElements={[
					{
						value: "contracts",
						label: "Contratti",
						onClick: () => setFinanceView("contracts"),
					},
					{
						value: "movements",
						label: "Movimenti di mercato",
						onClick: () => setFinanceView("movements"),
					},
				]}
				currentListElement={financeView}
				actions={<MainActions actions={headerActions} />}
			/>
			<div className="finance-page">
				{financeView === "contracts" && (
					<>
						{rosters.length > 0 && (
							<div className="finance-page__filter">
								<VLFSelectNew
									label="Squadra"
									options={rosterOptions}
									value={
										rosterOptions.find((o) => o.value === selectedRosterId) ||
										rosterOptions[0]
									}
									onChange={(v) => setSelectedRosterId(v?.value || null)}
									isClearable={false}
									sorted={false}
								/>
							</div>
						)}
						<KpiRow>
							<Kpi label="Contratti attivi" value={filteredContracts.length} />
							<Kpi label="Ingaggi al mese" value={fmt(totalMonthly)} />
							<Kpi label="Ingaggi all'anno" value={fmt(totalAnnual)} />
							<Kpi label="Ingaggio medio al mese" value={fmt(avgSalary)} />
							<Kpi
								label="Budget ingaggi mensile"
								value={budget != null ? fmt(budget) : "—"}
								tone={
									budget == null
										? undefined
										: budgetDiff > 0
											? "critical"
											: "good"
								}
								note={
									budget == null
										? "da impostare con la matita in alto (salvato solo su questo computer)"
										: budgetDiff > 0
											? `${fmt(budgetDiff)} oltre il budget`
											: `${fmt(Math.abs(budgetDiff))} disponibili`
								}
							/>
						</KpiRow>

						<div className="finance-page__grid">
							<div>
								<VLFCard
									title="Più pagati"
									content={
										<div className="finance-bars">
											{topEarners.length === 0 ? (
												<EmptyData>Nessun contratto con ingaggio.</EmptyData>
											) : (
												topEarners.map((c) => (
													<div
														key={c.id}
														className="finance-bars__row is-clickable"
														onClick={() => toPlayer(c)}
													>
														<span className="finance-bars__label">
															{contractName(c)}
														</span>
														<DsBar
															value={c.totalMonthlySalary}
															max={maxEarner}
														/>
														<span className="finance-bars__value">
															{fmt(c.totalMonthlySalary)}
														</span>
													</div>
												))
											)}
										</div>
									}
								/>
							</div>
							<div>
								<VLFCard
									title="Fasce di ingaggio"
									content={
										<div className="finance-bars">
											<div className="finance-bars__types">
												{byType.map(({type, count}) => (
													<StatusBadge
														key={type}
														label={`${count} ${contractTypeLabels[type] || type}`}
													/>
												))}
											</div>
											{salaryBands.map((band) => (
												<div key={band.label} className="finance-bars__row">
													<span className="finance-bars__label">
														{band.label}
													</span>
													<DsBar value={band.count} max={maxBandCount} />
													<span className="finance-bars__value">
														{band.count}
													</span>
												</div>
											))}
										</div>
									}
								/>
							</div>
						</div>

						{expiringSoon.length > 0 && (
							<VLFCard
								title="In scadenza entro 12 mesi"
								content={
									<DataTable
										columns={[
											{
												key: "name",
												label: "Giocatore",
												render: contractName,
												sortValue: contractName,
											},
											{
												key: "months",
												label: "Mancano",
												render: (c) => {
													const months = moment(c.endDate).diff(
														moment(),
														"months",
													);
													return (
														<StatusBadge
															tone={months <= 6 ? "critical" : "warning"}
															label={
																months === 0
																	? "meno di un mese"
																	: `${months} mes${months === 1 ? "e" : "i"}`
															}
														/>
													);
												},
												sortValue: (c) => c.endDate,
											},
											{
												key: "salary",
												label: "Ingaggio al mese",
												align: "end",
												render: (c) => fmt(c.totalMonthlySalary),
											},
											{
												key: "end",
												label: "Scadenza",
												render: (c) => moment(c.endDate).format(dateFormat),
											},
										]}
										rows={expiringSoon}
										onRowClick={toPlayer}
									/>
								}
							/>
						)}

						<div className="finance-page__grid">
							<div>
								<VLFCard
									title="Spesa per anno"
									content={
										<DataTable
											columns={[
												{key: "year", label: "Anno", sortable: true},
												{
													key: "contractCount",
													label: "Contratti",
													align: "center",
												},
												{
													key: "totalCost",
													label: "Costo totale",
													align: "end",
													render: (r) => fmt(r.totalCost),
													sortable: true,
												},
											]}
											rows={yearRows.map((r) => ({...r, id: r.year}))}
											empty="Nessun contratto con ingaggio."
										/>
									}
								/>
							</div>
							<div>
								<VLFCard
									title="Giocatori"
									content={
										<DataTable
											columns={[
												{
													key: "name",
													label: "Giocatore",
													render: contractName,
													sortValue: contractName,
												},
												{
													key: "type",
													label: "Tipo",
													render: (c) =>
														contractTypeLabels[c.contractType] ||
														c.contractType,
												},
												{
													key: "endDate",
													label: "Scadenza",
													render: (c) =>
														c.endDate
															? moment(c.endDate).format(dateFormat)
															: "—",
													sortable: true,
												},
												{
													key: "totalMonthlySalary",
													label: "Al mese",
													align: "end",
													render: (c) => fmt(c.totalMonthlySalary),
													sortable: true,
												},
											]}
											rows={sortedContracts}
											onRowClick={toPlayer}
											empty="Nessun contratto attivo."
										/>
									}
								/>
							</div>
						</div>
					</>
				)}

				{financeView === "movements" && (
					<>
						<div>
							<StatusBadge
								tone="warning"
								label="Movimenti salvati solo su questo computer finché il server non li gestisce"
							/>
						</div>
						<KpiRow>
							<Kpi label="Entrate" value={fmt(mvKpi.entrate)} tone="good" />
							<Kpi label="Uscite" value={fmt(mvKpi.uscite)} tone="critical" />
							<Kpi label="Saldo" value={fmtSaldo(mvKpi.saldo)} />
						</KpiRow>
						<VLFCard
							content={
								<DataTable
									columns={[
										{
											key: "date",
											label: "Data",
											render: (m) => moment(m.date).format(dateFormat),
											sortable: true,
										},
										{key: "playerName", label: "Giocatore", sortable: true},
										{
											key: "type",
											label: "Tipo",
											render: (m) =>
												MOVEMENT_TYPES.find((t) => t.value === m.type)?.label ||
												m.type,
										},
										{
											key: "amount",
											label: "Importo",
											align: "end",
											render: (m) => (
												<span
													className={
														"finance-amount finance-amount--" + m.direction
													}
												>
													{m.direction === "in" ? "+" : "−"}
													{fmt(m.amount)}
												</span>
											),
											sortValue: (m) =>
												m.direction === "in" ? m.amount : -m.amount,
										},
										{key: "note", label: "Note", render: (m) => m.note || "—"},
										{
											key: "actions",
											label: "",
											align: "end",
											render: (m) => (
												<div onClick={(e) => e.stopPropagation()}>
													<MainActions
														actions={[
															{
																icon: <DeleteIcon />,
																title: "Elimina",
																variant: "outlined",
																color: "error",
																onClick: () => setDeleteConfirm(m.id),
															},
														]}
													/>
												</div>
											),
										},
									]}
									rows={sortedMovements}
									onRowClick={(m) => {
										setEditingMovement(m);
										setMovementModal(true);
									}}
									empty="Nessun movimento: aggiungi acquisti, cessioni e bonus con il pulsante in alto."
								/>
							}
						/>
					</>
				)}
			</div>

			<MovementModal
				open={movementModal}
				onClose={() => {
					setMovementModal(false);
					setEditingMovement(null);
				}}
				onSave={handleSaveMovement}
				initialData={editingMovement}
			/>

			<VLFModal
				open={Boolean(deleteConfirm)}
				title="Eliminazione"
				content={<div className="mt-2">Eliminare il movimento?</div>}
				confirm={() => handleDeleteMovement(deleteConfirm)}
				close={() => setDeleteConfirm(null)}
				confirmLabel="Elimina"
			/>

			<VLFModal
				maxWidth="xs"
				open={budgetEditOpen}
				title="Budget ingaggi mensile"
				content={
					<VLFTextField
						label="Budget al mese (€)"
						type="number"
						placeholder="es. 25000"
						value={budgetInput}
						onChange={(e) => setBudgetInput(e.target.value)}
					/>
				}
				confirm={handleSaveBudget}
				close={() => setBudgetEditOpen(false)}
			/>
		</Page>
	);
}

export default FinancePage;
